import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { colors } from '@karnott/colors';
import { GOOGLE_MAX_ZOOM, IGN_MAX_ZOOM, MAP_TILES } from '../../../../constants/mapConstants';
import { PluginsContext } from '../../../../contexts/PluginsProvider';
import { I18n } from '../../../../i18n';
import { ANALYTICS, sendAnalyticsBtnClickEvent } from '../../../../utils/AnalyticsConstants';
import { L as Leaflet, setSmoothZoom } from '../../../../utils/LeafletOverrides';
import { getEmptyTiles, getGoogleMutantTiles, getIGNTiles } from '../../../../utils/map';
import { localStorageKeys } from '../../../../utils/storage';
import { useMapCarrier, useMapInCarrier } from '../../effects';
import Context from './Context';
import { useRegionBinder } from './effects';

export default function Provider({ children, id, allowAnyZoom = false, initialTiles: initialTilesProp }) {
  const carrier = useMapCarrier({ id });

  const ignTiles = useRef(getIGNTiles());
  const googleMutantTiles = useRef(getGoogleMutantTiles());
  const emptyTiles = useRef(getEmptyTiles());
  const tilesByName = useRef({
    [MAP_TILES.GOOGLE]: googleMutantTiles,
    [MAP_TILES.IGN]: ignTiles,
    [MAP_TILES.NONE]: emptyTiles,
  });

  const { hasOverallIgnTilesPlugin } = useContext(PluginsContext);
  const initialTiles = useMemo(() => {
    if (initialTilesProp && tilesByName.current[initialTilesProp]) return tilesByName.current[initialTilesProp].current;
    if (hasOverallIgnTilesPlugin && localStorage.getItem(localStorageKeys.ign) === 'true') {
      return ignTiles.current;
    }
    return googleMutantTiles.current;
  }, [hasOverallIgnTilesPlugin, initialTilesProp]);

  const [map, changeTiles] = useMapInCarrier({ carrier, tiles: initialTiles, allowAnyZoom });

  const initialUseIgn = useRef(localStorage.getItem(localStorageKeys.ign) === 'true');
  useEffect(() => {
    if (initialUseIgn.current) {
      map.setMaxZoom(IGN_MAX_ZOOM);
    } else {
      map.setMaxZoom(GOOGLE_MAX_ZOOM);
    }
  }, [map]);
  const [useIGN, setIGN] = useState(localStorage.getItem(localStorageKeys.ign) === 'true');

  const setTiles = useCallback(
    (tiles) => {
      if (tiles === MAP_TILES.GOOGLE) {
        setSmoothZoom(false, map);
        if (allowAnyZoom) {
          map.options.zoomSnap = 0;
          map.options.zoomDelta = 0.5;
        }
        map.setMaxZoom(GOOGLE_MAX_ZOOM);
        sendAnalyticsBtnClickEvent(window)(ANALYTICS.LABEL.MAP.SATELLITE_TILES);
        changeTiles(googleMutantTiles.current);
        setIGN(false);
      } else if (tiles === MAP_TILES.IGN) {
        setSmoothZoom(true, map);
        map.setMaxZoom(IGN_MAX_ZOOM);
        sendAnalyticsBtnClickEvent(window)(ANALYTICS.LABEL.MAP.IGN_TILES);
        changeTiles(ignTiles.current);
        setIGN(true);
      } else if (tiles === MAP_TILES.NONE) {
        setSmoothZoom(true, map);
        map.setMaxZoom(GOOGLE_MAX_ZOOM);
        changeTiles(emptyTiles.current);
        setIGN(false);
      }
    },
    [allowAnyZoom, changeTiles, map],
  );

  const toggleTiles = useCallback(() => {
    if (useIGN) {
      setTiles(MAP_TILES.GOOGLE);
    } else {
      setTiles(MAP_TILES.IGN);
    }
  }, [useIGN, setTiles]);

  const regionBinder = useRegionBinder(map);

  const line = useMemo(
    () =>
      new Leaflet.Draw.Polyline(map, {
        shapeOptions: {
          color: colors('orange'),
          weight: 4,
        },
      }),
    [map],
  );

  const poly = useMemo(
    () =>
      new Leaflet.Draw.Polygon(map, {
        shapeOptions: {
          color: colors('orange'),
          fillColor: colors('green'),
          opacity: 0.5,
          weight: 2,
        },
        allowIntersection: false,
        touchIcon: new Leaflet.DivIcon({
          iconSize: new Leaflet.Point(6, 6),
          className: 'leaflet-div-icon leaflet-editing-icon',
        }),
        icon: new Leaflet.DivIcon({
          iconSize: new Leaflet.Point(6, 6),
          className: 'leaflet-div-icon leaflet-editing-icon',
        }),
        drawError: {
          message: I18n.t('DrawParcel.intersects_unallocated'),
        },
      }),
    [map],
  );

  return (
    <Context.Provider
      value={{
        line,
        map,
        carrier,
        regionBinder,
        poly,
        toggleTiles,
        setTiles,
        ignTiles,
        googleMutantTiles,
        emptyTiles,
      }}
    >
      {children}
    </Context.Provider>
  );
}
